// api
import type { GetCapabilityBaseDto } from '@/api/generated/recalc/endpoints/v2/devices';

// libraries
import { z } from 'zod';

export type CapabilityEditMode = 'readonly' | 'drawer';

export type CapabilityPolicy = {
  capabilityType: string;
  canEdit: boolean;
  canDelete: boolean;
  editMode: CapabilityEditMode;
  editHandler?:
    | 'putAnalogDataChannel'
    | 'putCalibratableElectricCurrentDataChannels'
    | 'putCalibratableFlowDataChannels'
    | 'putDataIntervalConfiguration'
    | 'putHeatingControl'
    | 'putMBus'
    | 'postOptoEye'
    | 'putPluginConfiguration'
    | 'unknown';
  deleteHandler?:
    | 'deleteHeatingControlActuatorChannelWeekplan'
    | 'deleteOptoEye'
    | 'unknown';
};

const capabilityPolicySchema = z.object({
  capabilityType: z.string().min(1),
  canEdit: z.boolean(),
  canDelete: z.boolean(),
  editMode: z.enum(['readonly', 'drawer']),
  editHandler: z
    .enum([
      'putAnalogDataChannel',
      'putCalibratableElectricCurrentDataChannels',
      'putCalibratableFlowDataChannels',
      'putDataIntervalConfiguration',
      'putHeatingControl',
      'putMBus',
      'postOptoEye',
      'putPluginConfiguration',
      'unknown',
    ])
    .optional(),
  deleteHandler: z
    .enum([
      'deleteHeatingControlActuatorChannelWeekplan',
      'deleteOptoEye',
      'unknown',
    ])
    .optional(),
});

function definePolicy(policy: CapabilityPolicy) {
  return capabilityPolicySchema.parse(policy);
}

const defaultPolicy = definePolicy({
  capabilityType: 'Unknown',
  canEdit: false,
  canDelete: false,
  editMode: 'readonly',
});

const capabilityPolicies: Record<string, CapabilityPolicy> = {
  AnalogDataChannels: definePolicy({
    capabilityType: 'AnalogDataChannels',
    canEdit: true,
    canDelete: false,
    editMode: 'drawer',
    editHandler: 'putAnalogDataChannel',
  }),
  BasicDataChannels: definePolicy({
    capabilityType: 'BasicDataChannels',
    canEdit: false,
    canDelete: false,
    editMode: 'readonly',
  }),
  Battery: definePolicy({
    capabilityType: 'Battery',
    canEdit: false,
    canDelete: false,
    editMode: 'readonly',
  }),
  CalibratableElectricCurrentDataChannels: definePolicy({
    capabilityType: 'CalibratableElectricCurrentDataChannels',
    canEdit: true,
    canDelete: false,
    editMode: 'drawer',
    editHandler: 'putCalibratableElectricCurrentDataChannels',
  }),
  CalibratableFlowDataChannels: definePolicy({
    capabilityType: 'CalibratableFlowDataChannels',
    canEdit: true,
    canDelete: false,
    editMode: 'drawer',
    editHandler: 'putCalibratableFlowDataChannels',
  }),
  DataIntervalConfiguration: definePolicy({
    capabilityType: 'DataIntervalConfiguration',
    canEdit: true,
    canDelete: false,
    editMode: 'drawer',
    editHandler: 'putDataIntervalConfiguration',
  }),
  Gateway: definePolicy({
    capabilityType: 'Gateway',
    canEdit: false,
    canDelete: false,
    editMode: 'readonly',
  }),
  HeatingControl: definePolicy({
    capabilityType: 'HeatingControl',
    canEdit: true,
    canDelete: true,
    editMode: 'drawer',
    editHandler: 'putHeatingControl',
    deleteHandler: 'deleteHeatingControlActuatorChannelWeekplan',
  }),
  MBusSensor: definePolicy({
    capabilityType: 'MBusSensor',
    canEdit: true,
    canDelete: false,
    editMode: 'drawer',
    editHandler: 'putMBus',
  }),
  OptoEye: definePolicy({
    capabilityType: 'OptoEye',
    canEdit: true,
    canDelete: true,
    editMode: 'drawer',
    editHandler: 'postOptoEye',
    deleteHandler: 'deleteOptoEye',
  }),
  PluginModule: definePolicy({
    capabilityType: 'PluginModule',
    canEdit: true,
    canDelete: false,
    editMode: 'drawer',
    editHandler: 'putPluginConfiguration',
  }),
  ProductionInfo: definePolicy({
    capabilityType: 'ProductionInfo',
    canEdit: true,
    canDelete: false,
    editMode: 'drawer',
    editHandler: 'unknown',
  }),
  Rfu: definePolicy({
    capabilityType: 'Rfu',
    canEdit: false,
    canDelete: false,
    editMode: 'readonly',
  }),
};

export function resolveCapabilityType(
  capabilityName: string,
  capability: GetCapabilityBaseDto,
) {
  const rawCapabilityType = capability.$type?.trim();

  if (rawCapabilityType) {
    return rawCapabilityType;
  }

  return capabilityName;
}

export function getCapabilityPolicy(
  capabilityName: string,
  capability: GetCapabilityBaseDto,
): CapabilityPolicy {
  const capabilityType = resolveCapabilityType(capabilityName, capability);

  return (
    capabilityPolicies[capabilityType] ?? {
      ...defaultPolicy,
      capabilityType,
    }
  );
}
